import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { today, toISODate } from '@/lib/clock';
import { billableDays } from '@/lib/rental';
import type { VehicleSearch } from '@/services/domain';
import { readStored, writeStored } from '@/services/storage';
import { defaultVehicleSearch } from './defaults';

interface VehicleSearchState {
  search: VehicleSearch;
  /** Merges a partial update (e.g. from URL params or the search sheet). */
  update: (patch: Partial<VehicleSearch>) => void;
  /** Billable days, display only (the quote is the price of record). */
  days: number;
}

const Ctx = createContext<VehicleSearchState | null>(null);

function isValid(s: VehicleSearch) {
  return s.start >= toISODate(today()) && s.end > s.start;
}

/** The current vehicle search, shared by the header pill, the search sheet, results, vehicle page and checkout. */
export function VehicleSearchProvider({ children }: { children: ReactNode }) {
  const [search, setSearch] = useState<VehicleSearch>(() => {
    const stored = readStored<VehicleSearch | null>('vehicleSearch', null);
    return stored && isValid(stored) ? stored : defaultVehicleSearch();
  });
  useEffect(() => writeStored('vehicleSearch', search), [search]);
  const update = useCallback((patch: Partial<VehicleSearch>) => setSearch((s) => ({ ...s, ...patch })), []);
  const days = billableDays(search.start, search.end, search.pickupTime, search.returnTime);
  const value = useMemo(() => ({ search, update, days }), [search, update, days]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useVehicleSearch(): VehicleSearchState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useVehicleSearch must be used inside <VehicleSearchProvider>');
  return ctx;
}
